<template>
  <div class="row" justify="center">
    <v-col md="12">
      <v-breadcrumbs :items="items">
        <template v-slot:divider>
          <v-icon>mdi-forward</v-icon>
        </template>
      </v-breadcrumbs>
    </v-col>

    <v-col md="12">
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon left color="white">mdi-book-open-page-variant</v-icon>
          Reflective Logs
        </v-card-title>
        <v-card-text>
          <p class="my-4">
            Select a record to view instructor and learner reflective logs and
            additional notes.
          </p>

          <v-data-table
            :headers="headers"
            :items="bookingFeedbackList"
            :search="search"
            item-key="booking_id"
            :loading="tableLoading"
            loading-text="Loading booking feedback list. Please wait."
            @click:row="openReflectiveDialog"
            class="elevation-1"
          >
            <template v-slot:top>
              <v-text-field
                v-model="search"
                label="Search"
                placeholder="Enter keyword to search"
                class="ma-4"
                clearable
                prepend-inner-icon="mdi-magnify"
              ></v-text-field>
            </template>

            <template v-slot:[`item.booking_id`]="{ item }">
              <strong>B{{ item.booking_id }}</strong>
            </template>

            <template v-slot:[`item.adi_id`]="{ item }">
              <span>A{{ item.adi_id }}</span>
            </template>

            <template v-slot:[`item.learner_id`]="{ item }">
              <span>L{{ item.learner_id }}</span>
            </template>

            <template v-slot:[`item.added_on`]="{ item }">
              {{ item.added_on | dateReOrderRMtime }}
            </template>

            <template v-slot:no-data>
              <v-alert type="info" outlined class="ma-4">
                No booking feedback records available.
              </v-alert>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-col>

    <ReflectiveThreadDialog
      :dialog="dialog"
      :booking-summary="selectedBooking"
      :thread-bubbles="threadBubbles"
      :loading="dialogLoading"
      @close="closeDialog"
    />
  </div>
</template>

<script>
import ReflectiveThreadDialog from "@/components/Reflective/ReflectiveThreadDialog.vue";
import { mapActions } from "vuex";
import {
  M_OU,
  LOAD_REFLECTIVE_LOGS_LIST,
  GET_REFLECTIVE_LOG_FULL_DATA,
  GET_REFLECTIVE_LOGS_LIST,
} from "@/store/mutation-list";
import { mapGetters } from "vuex/dist/vuex.common.js";

export default {
  name: "ReflectiveLogs",
  components: { ReflectiveThreadDialog },
  data() {
    return {
      items: [
        {
          text: "Dashboard",
          disabled: false,
          href: "/dashboard/home",
        },
        {
          text: "Reflective Logs",
          disabled: true,
          href: "/dashboard/reflectivelogs",
        },
      ],
      headers: [
        { text: "Booking", value: "booking_id" },
        { text: "Instructor ID", value: "adi_id" },
        { text: "Instructor", value: "adi_name" },
        { text: "Learner ID", value: "learner_id" },
        { text: "Learner", value: "learner_name" },
        { text: "Added On", value: "added_on" },
      ],
      tableLoading: false,
      dialogLoading: false,
      dialog: false,
      search: "",
      //   bookingFeedbackList: [],
      selectedBooking: null,
      threadBubbles: [],
    };
  },
  methods: {
    ...mapActions({
      loadReflectiveLogsList: `${M_OU}${LOAD_REFLECTIVE_LOGS_LIST}`,
      getReflectiveLogDetails: `${M_OU}${GET_REFLECTIVE_LOG_FULL_DATA}`,
    }),

    openReflectiveDialog(item) {
      this.selectedBooking = item;
      this.dialog = true;
      this.dialogLoading = true;
      this.threadBubbles = [];

      this.getReflectiveLogDetails(item.booking_id)
        .then((payload) => {
          this.threadBubbles = this.buildThreadBubbles(payload);
        })
        .finally(() => {
          this.dialogLoading = false;
        });
    },

    closeDialog() {
      this.dialog = false;
      this.selectedBooking = null;
      this.threadBubbles = [];
      this.dialogLoading = false;
    },

    buildThreadBubbles(payload) {
      const bubbles = [];
      const instructorLogs = payload.instructor_logs || [];
      const learnerLogs = payload.learner_logs || [];
      const instructorNotes = payload.instructor_notes || [];
      const learnerNotes = payload.learner_notes || [];

      bubbles.push(...this.groupLogBubbles(instructorLogs, "instructor"));
      bubbles.push(...this.groupLogBubbles(learnerLogs, "learner"));
      bubbles.push(...this.buildNoteBubbles(instructorNotes, "instructor"));
      bubbles.push(...this.buildNoteBubbles(learnerNotes, "learner"));

      return bubbles.sort((a, b) => {
        const aTime = this.toTime(a.created_on);
        const bTime = this.toTime(b.created_on);
        return aTime - bTime;
      });
    },

    groupLogBubbles(records, actorType) {
      const bubbles = [];
      let currentBubble = null;
      let bubbleIndex = 0;

      records.forEach((record) => {
        if (!currentBubble || currentBubble.created_on !== record.created_on) {
          bubbleIndex += 1;
          currentBubble = {
            id: `${actorType}-logs-${bubbleIndex}`,
            actor_type: actorType,
            kind: "logs",
            created_on: record.created_on,
            status: record.status,
            records: [record],
            note: "",
          };
          bubbles.push(currentBubble);
          return;
        }

        currentBubble.records.push(record);
        if (record.status === 0) {
          currentBubble.status = 0;
        }
      });

      return bubbles;
    },

    buildNoteBubbles(records, actorType) {
      return records.map((record, index) => ({
        id: `${actorType}-note-${index + 1}`,
        actor_type: actorType,
        kind: "note",
        created_on: record.created_on,
        status: record.status,
        records: [],
        note: record.note,
      }));
    },

    toTime(dateTime) {
      return new Date(dateTime.replace(" ", "T")).getTime();
    },
  },
  computed: {
    ...mapGetters({
      bookingFeedbackList: `${M_OU}${GET_REFLECTIVE_LOGS_LIST}`,
    }),
  },
  created() {
    this.loadReflectiveLogsList();
  },
};
</script>

<style scoped></style>
